import { ref, computed, watch } from 'vue'
import { SqlDatasourceApi } from '@/services'
import useLogger from '@/composables/useLogger'

export function useSqlDatasource () {
  const logger = useLogger('useSqlDatasource')
  const sqlDatasources = ref([])
  const isLoading = ref(false)

  function useModel (sqlDatasource) {
    // Configure the initial query_param settings
    if (!sqlDatasource.params.sql) {
      sqlDatasource.params.sql = ''
    }

    if (!sqlDatasource.params.connection_name) {
      sqlDatasource.params.connection_name = 'luther_strategy'
    }
    const isDirty = ref(false)
    const model = ref(sqlDatasource)

    // params is the datasource properties such as sql
    // meta is information about the datasource or it's parameters
    const name = computed(buildAccessor('name'))
    const dealerId = computed(buildAccessor('dealer_id'))
    const sql = computed(buildNestedAccessor('params', 'sql'))
    const headers = computed(buildNestedAccessor('meta', 'headers'))
    const connectionName = computed(buildNestedAccessor('params', 'connection_name'))

    watch(model, (newVal, oldVal) => {
      isDirty.value = true
      logger.trace('Model is now dirty')
    }, { deep: true })

    async function save () {
      const payload = {
        name: model.value.name,
        query_params: model.value.params,
        dealer_id: dealerId.value,
        meta: model.value.meta
      }
      if (model.value.id) {
        payload.id = model.value.id
      }
      const newDs = await SqlDatasourceApi.saveSqlDatasource(payload)
      return useModel(newDs.data)
    }

    async function getLinkForCsvDownload () {
      const modelCopy = { ...model.value }
      modelCopy.query_params = { ...sqlDatasource.params, sql: sql.value, connection_name: connectionName.value }

      const resp = await SqlDatasourceApi.executeQuery('csv', modelCopy)
      return resp.data.csv_url
    }

    async function runQuery () {
      const modelCopy = { ...model.value }
      modelCopy.query_params = { ...sqlDatasource.params, sql: sql.value, connection_name: connectionName.value }

      const resp = await SqlDatasourceApi.executeQuery('json', modelCopy)
      // returning resp so we can see the meta data { data: {...}, metadata: {...} }
      return resp
    }

    /**
     * private
     */
    function buildAccessor (field) {
      return {
        get () {
          logger.trace('getField', model.value[field])
          return model.value[field]
        },
        set (value) {
          logger.trace('setField', field, value)
          model.value = { ...model.value, ...{ [field]: value } }
        }
      }
    }

    function buildNestedAccessor (modelField, field) {
      return {
        get () {
          logger.trace('getField', model.value[modelField][field])
          return model.value[modelField][field]
        },
        set (value) {
          logger.trace('setField', modelField, field, value)
          model.value[modelField] = { ...model.value[modelField], ...{ [field]: value } }
        }
      }
    }

    return {
      name,
      sql,
      headers,
      connectionName,
      save,
      getLinkForCsvDownload,
      runQuery,
      isDirty
    }
  }

  async function fetchAll (queryParams = {}) {
    try {
      isLoading.value = true
      sqlDatasources.value = (await SqlDatasourceApi.fetchSqlDatasources(queryParams)).data
      return sqlDatasources
    } finally {
      isLoading.value = false
    }
  }

  async function loadSqlDatasource (sqlDatasourceId) {
    const sqlDs = (await SqlDatasourceApi.getSqlDatasource(sqlDatasourceId)).data
    return useModel(sqlDs)
  }

  async function loadAvailableConnections () {
    const connections = await SqlDatasourceApi.getAvailableConnections()
    return connections.data
  }

  async function buildNewSqlDatasource (dealerId) {
    const ds = {
      name: '',
      dealer_id: dealerId,
      params: {
        sql: '',
        connection_name: 'luther_strategy'
      }
    }
    return useModel(ds)
  }

  async function deleteSqlDatasource (sqlDatasourceId) {
    return SqlDatasourceApi.deleteSqlDatasource(sqlDatasourceId)
  }

  return {
    sqlDatasources,
    loadSqlDatasource,
    fetchAll,
    isLoading,
    buildNewSqlDatasource,
    deleteSqlDatasource,
    loadAvailableConnections
  }
}
